// wLipSync (github.com/mrxz/wlipsync, MIT): WASM port of uLipSync. Matches each 16 ms of sound against a
// vowel profile (A I U E O + S hiss) with MFCCs; loudness sets how open the mouth is. ~13 KB in total.
// This is the app's lip sync (chosen 2026-10-16). Files: vendor/wlipsync, served at /lipsync/wlipsync/.
import type { Cue, Viseme } from "../../model/face";

export const RATE = 16000;

// How open each shape is, to pick which neighbour a too-short shape merges into.
const OPENNESS: Record<Viseme, number> = { X: 0, A: 0, B: 1, G: 1, F: 1, H: 2, C: 2, E: 2, D: 3 };

// Smoothing applied to wLipSync's raw 16 ms decisions (the "flapping" fix, 2026-10-16). All in milliseconds.
const TUNING = {
  smoothMs: 64, // moving average on loudness, so openness (B/C/D) doesn't jitter with every syllable peak
  voteMs: 96, // each moment takes the most common vowel in this window, so one odd frame can't switch shape
  minHoldMs: 100, // shortest a shape may stay on screen; shorter ones merge into the neighbour closest in openness
  minRestMs: 80, // shortest closed mouth (X) kept inside speech; shorter gaps stay open
};

// Runs shorter than the hold merge, shortest first, into the neighbour closest in openness.
function mergeShortRuns(shapes: Viseme[], step: number, minHoldMs: number, minRestMs: number): Cue[] {
  const runs: { v: Viseme; n: number }[] = [];
  for (const v of shapes) {
    const r = runs[runs.length - 1];
    if (r && r.v === v) r.n++;
    else runs.push({ v, n: 1 });
  }
  const minRun = Math.max(1, Math.round(minHoldMs / 1000 / step));
  const minRest = Math.round(minRestMs / 1000 / step);
  const tooShort = (r: { v: Viseme; n: number }, i: number) =>
    r.v === "X" ? i > 0 && i < runs.length - 1 && r.n < minRest : r.n < minRun;
  for (;;) {
    let k = -1;
    runs.forEach((r, i) => { if (runs.length > 1 && tooShort(r, i) && (k < 0 || r.n < runs[k]!.n)) k = i; });
    if (k < 0) break;
    const r = runs[k]!, prev = runs[k - 1], next = runs[k + 1];
    const d = (o?: { v: Viseme }) => (o ? Math.abs(OPENNESS[o.v] - OPENNESS[r.v]) : Infinity);
    const into = d(prev) <= d(next) ? prev! : next!;
    into.n += r.n;
    runs.splice(k, 1);
    // Neighbours that now touch and match become one run.
    for (let i = runs.length - 1; i > 0; i--) if (runs[i]!.v === runs[i - 1]!.v) { runs[i - 1]!.n += runs[i]!.n; runs.splice(i, 1); }
  }
  const cues: Cue[] = [];
  let t = 0;
  for (const r of runs) {
    cues.push({ start: +t.toFixed(3), end: +(t + r.n * step).toFixed(3), value: r.v });
    t += r.n * step;
  }
  return cues;
}

type WlExports = {
  load_profile(...a: number[]): number; get_profile_ptrs(): number; set_input(rate: number): void;
  get_input_buffer(): number; get_input_buffer_size(): number; get_volume_ptr(): number; execute(index: number): number;
};
type WlProfile = { targetSampleRate: number; sampleCount: number; melFilterBankChannels: number; compareMethod: number; names: string[]; mfccDataCount: number; useStandardization: boolean; values: number[][]; means: number[]; stdDevs: number[] };
let wlMods: Promise<{ module: WebAssembly.Module; profile: WlProfile }> | null = null;

// wLipSync's binary profile format (same as its parseBinaryProfile).
function parseProfile(buf: ArrayBuffer): WlProfile {
  const v = new DataView(buf);
  let n = 0;
  const f32 = () => { const x = v.getFloat32(n); n += 4; return x; };
  const u8 = () => v.getUint8(n++);
  const i32 = () => { const x = v.getInt32(n); n += 4; return x; };
  const sig = String.fromCharCode(u8(), u8(), u8(), u8());
  if (sig !== "WLIP") throw new Error("Not a wLipSync profile");
  const targetSampleRate = i32(), sampleCount = i32(), melFilterBankChannels = u8(), compareMethod = u8();
  const count = u8(), mfccDataCount = u8(), useStandardization = u8() === 1;
  const values = [...Array(count)].map(() => [...Array(12)].map(f32));
  const means = [...Array(12)].map(f32), stdDevs = [...Array(12)].map(f32);
  const names = [...Array(count)].map(() => { const len = u8(); const s = new TextDecoder().decode(buf.slice(n, n + len)); n += len; return s; });
  return { targetSampleRate, sampleCount, melFilterBankChannels, compareMethod, names, mfccDataCount, useStandardization, values, means, stdDevs };
}

function loadWl() {
  if (!wlMods) {
    wlMods = (async () => {
      const base = "/lipsync/wlipsync/";
      const [module, profileBuf] = await Promise.all([
        WebAssembly.compileStreaming(fetch(base + "wlipsync.wasm")),
        fetch(base + "profile.bin").then(r => r.arrayBuffer()),
      ]);
      return { module, profile: parseProfile(profileBuf) };
    })();
    wlMods.catch(() => (wlMods = null));
  }
  return wlMods;
}

// Volume bounds wLipSync's own node uses, in log10(RMS): at or below min is silence, at max fully open.
const WL_MIN_VOL = -2.5, WL_MAX_VOL = -1.5;

const openOf = (logRms: number) => Math.max(0, Math.min(1, (logRms - WL_MIN_VOL) / (WL_MAX_VOL - WL_MIN_VOL)));

function vowelToShape(name: string, open: number): Viseme {
  if (open <= 0) return "X";
  if (open < 0.15) return "B";
  switch (name) {
    case "A": return open > 0.55 ? "D" : "C";
    case "E": return open > 0.4 ? "C" : "B";
    case "I": return "B";
    case "U": return "F";
    case "O": return "E";
    default: return "B"; // S (hiss) and anything else
  }
}

// wLipSync's raw evidence, one entry per 16 ms frame: the closest vowel class (A I U E O, or S for hiss) and
// loudness as log10(RMS). openOf(loud) turns loudness into 0 (silence) .. 1 (fully open).
async function wlFrames(pcm: Float32Array): Promise<{ vowels: string[]; loud: number[]; step: number }> {
  const { module, profile: p } = await loadWl();
  // Same setup as wLipSync's audio worklet processor, called directly instead of from a worklet.
  const memory = new WebAssembly.Memory({ initial: 4 });
  const inst = await WebAssembly.instantiate(module, { env: { memory } });
  const x = inst.exports as unknown as WlExports;
  x.load_profile(p.targetSampleRate, p.sampleCount, p.melFilterBankChannels, p.compareMethod, p.names.length, p.mfccDataCount, p.useStandardization ? 1 : 0);
  const ptrs = new DataView(memory.buffer, x.get_profile_ptrs(), 12);
  const mfcc = new Float32Array(memory.buffer, ptrs.getInt32(0, true), p.names.length * 12);
  p.values.forEach((row, i) => mfcc.set(row, i * 12));
  new Float32Array(memory.buffer, ptrs.getInt32(4, true), 12).set(p.means);
  new Float32Array(memory.buffer, ptrs.getInt32(8, true), 12).set(p.stdDevs);
  x.set_input(RATE);
  const size = x.get_input_buffer_size();
  const input = new Float32Array(memory.buffer, x.get_input_buffer(), size);
  const vol = new DataView(memory.buffer, x.get_volume_ptr(), 4);

  // Each decision looks at the last sampleCount samples (64 ms), so it describes the middle of that window.
  const hop = 256, step = hop / RATE, lag = p.sampleCount / 2;
  const n = Math.ceil(pcm.length / hop);
  const vowels: string[] = new Array(n).fill(""), loud: number[] = new Array(n).fill(-9);
  let idx = 0;
  const padded = new Float32Array(pcm.length + lag);
  padded.set(pcm);
  for (let i = 0; i < padded.length; i++) {
    input[idx] = padded[i]!;
    idx = (idx + 1) % size;
    if ((i + 1) % hop) continue;
    const which = x.execute(idx);
    const at = Math.round((i + 1 - lag) / hop);
    if (at < 0 || at >= n) continue;
    vowels[at] = p.names[which] ?? "";
    loud[at] = Math.log10(vol.getFloat32(0, true) || 1e-9);
  }
  return { vowels, loud, step };
}

// Mono 16 kHz sound -> mouth cues.
export async function wLipSync(pcm: Float32Array): Promise<Cue[]> {
  const { vowels, loud, step } = await wlFrames(pcm);
  const n = vowels.length;
  // Centred windows, in frames.
  const half = (ms: number) => Math.round(ms / 1000 / step / 2);
  const sh = half(TUNING.smoothMs), vh = half(TUNING.voteMs);
  const shapes: Viseme[] = vowels.map((_, i) => {
    let sum = 0, c = 0;
    for (let j = Math.max(0, i - sh); j <= Math.min(n - 1, i + sh); j++) { sum += loud[j]!; c++; }
    // Silence stays the frame's own: averaging would smear the mouth open into pauses.
    const open = openOf(loud[i]!) <= 0 ? 0 : openOf(sum / c);
    const votes = new Map<string, number>();
    for (let j = Math.max(0, i - vh); j <= Math.min(n - 1, i + vh); j++) if (openOf(loud[j]!) > 0) votes.set(vowels[j]!, (votes.get(vowels[j]!) ?? 0) + 1);
    let best = vowels[i]!, most = 0;
    for (const [v, k] of votes) if (k > most) { best = v; most = k; }
    return vowelToShape(best, open);
  });
  return mergeShortRuns(shapes, step, TUNING.minHoldMs, TUNING.minRestMs);
}
